
/**
 * A utility class for generating custom map pins as canvas elements.
 * <br /><br />
 * <div align='center'>
 * <img src='Images/PinBuilder.png' width='500'/><br />
 * Example pins generated using both the maki icon set, which ships with Cesium, and single character text.
 * </div>
 *
 * @alias PinBuilder
 * @constructor
 *
 * @demo {@link https://sandcastle.cesium.com/index.html?src=Map%20Pins.html|Cesium Sandcastle PinBuilder Demo}
 */
function PinBuilder () {
  this._cache = {}
}

/**
 * Creates an empty pin of the specified color and size.
 *
 * @param {Cesium.Color} color The color of the pin.
 * @param {Number} size The size of the pin, in pixels.
 * @returns {HTMLCanvasElement} The canvas element that represents the generated pin.
 */
PinBuilder.prototype.fromColor = function (color1, color2, color3, size) {
  // >>includeStart('debug', pragmas.debug);
  if (!Cesium.defined(color1)) {
    throw new Cesium.DeveloperError('color is required')
  }
  if (!Cesium.defined(size)) {
    throw new Cesium.DeveloperError('size is required')
  }
  // >>includeEnd('debug');
  return createPin(undefined, undefined, color1, color2, color3, size, this._cache)
}

/**
 * Creates a pin with the specified icon, color, and size.
 *
 * @param {Cesium.Resource|String} url The url of the image to be stamped onto the pin.
 * @param {Cesium.Color} color The color of the pin.
 * @param {Number} size The size of the pin, in pixels.
 * @returns {HTMLCanvasElement|Promise.<HTMLCanvasElement>} The canvas element or a Promise to the canvas element that represents the generated pin.
 */
PinBuilder.prototype.fromUrl = function (url, color1, color2, color3, size) {
  // >>includeStart('debug', pragmas.debug);
  if (!Cesium.defined(url)) {
    throw new Cesium.DeveloperError('url is required')
  }
  if (!Cesium.defined(color1)) {
    throw new Cesium.DeveloperError('color is required')
  }
  if (!Cesium.defined(size)) {
    throw new Cesium.DeveloperError('size is required')
  }
  // >>includeEnd('debug');
  return createPin(url, undefined, color1, color2, color3, size, this._cache)
}

/**
 * Creates a pin with the specified {@link https://www.mapbox.com/maki/|maki} icon identifier, color, and size.
 *
 * @param {String} id The id of the maki icon to be stamped onto the pin.
 * @param {Cesium.Color} color The color of the pin.
 * @param {Number} size The size of the pin, in pixels.
 * @returns {HTMLCanvasElement|Promise.<HTMLCanvasElement>} The canvas element or a Promise to the canvas element that represents the generated pin.
 */
PinBuilder.prototype.fromMakiIconId = function (id, color1, color2, color3, size) {
  // >>includeStart('debug', pragmas.debug);
  if (!Cesium.defined(id)) {
    throw new Cesium.DeveloperError('id is required')
  }
  if (!Cesium.defined(color1)) {
    throw new Cesium.DeveloperError('color is required')
  }
  if (!Cesium.defined(size)) {
    throw new Cesium.DeveloperError('size is required')
  }
  // >>includeEnd('debug');
  return createPin(
    Cesium.buildModuleUrl(`Assets/Textures/maki/${encodeURIComponent(id)}.png`),
    undefined,
    color1,
    color2,
    color3,
    size,
    this._cache
  )
}

/**
 * Creates a pin with the specified text, color, and size.  The text will be sized to be as large as possible
 * while still being contained completely within the pin.
 *
 * @param {String} text The text to be stamped onto the pin.
 * @param {Cesium.Color} color The color of the pin.
 * @param {Number} size The size of the pin, in pixels.
 * @returns {HTMLCanvasElement} The canvas element that represents the generated pin.
 */
PinBuilder.prototype.fromText = function (text, color1, color2, color3, size) {
  // >>includeStart('debug', pragmas.debug);
  if (!Cesium.defined(text)) {
    throw new Cesium.DeveloperError('text is required')
  }
  if (!Cesium.defined(color1)) {
    throw new Cesium.DeveloperError('color is required')
  }
  if (!Cesium.defined(size)) {
    throw new Cesium.DeveloperError('size is required')
  }
  // >>includeEnd('debug');

  return createPin(undefined, text, color1, color2, color3, size, this._cache)
}

// const colorScratch = new Cesium.Color()

// This function (except for the 3 commented lines) was auto-generated from an online tool,
// http://www.professorcloud.com/svg-to-canvas/, using Assets/Textures/pin.svg as input.
// The reason we simply can't load and draw the SVG directly to the canvas is because
// it taints the canvas in Internet Explorer (and possibly some other browsers); making
// it impossible to create a WebGL texture from the result.
function drawPin (ctx, color1, color2, color3, size) {
  const color1Css = color1.toCssColorString()
  const color2Css = color2.toCssColorString()
  const color3Css = color3.toCssColorString()
  ctx.save()
  ctx.scale(size / 32, size / 32) // Added to auto-generated code to scale up to desired size.
  ctx.strokeStyle = 'rgba(0,0,0,0)'
  ctx.miterLimit = 4
  ctx.font = "15px / 21.4286px ''"
  ctx.font = '   15px '
  ctx.save()
  ctx.fillStyle = color1Css
  ctx.font = '   15px '
  ctx.globalAlpha = 0.4
  ctx.beginPath()
  ctx.moveTo(16, 0)
  ctx.bezierCurveTo(24.837, 0, 32, 7.163, 32, 16)
  ctx.bezierCurveTo(32, 24.837, 24.837, 32, 16, 32)
  ctx.bezierCurveTo(7.163, 32, 0, 24.837, 0, 16)
  ctx.bezierCurveTo(0, 7.163, 7.163, 0, 16, 0)
  ctx.closePath()
  ctx.fill('evenodd')
  ctx.stroke()
  ctx.restore()
  ctx.save()
  ctx.fillStyle = color2Css
  ctx.font = '   15px '
  ctx.beginPath()
  ctx.moveTo(15.998, 3)
  ctx.bezierCurveTo(23.177, 3, 28.998, 8.82, 28.998, 16)
  ctx.bezierCurveTo(28.998, 23.179, 23.177, 28.999, 15.998, 28.999)
  ctx.bezierCurveTo(8.818, 28.999, 2.998, 23.179, 2.998, 16)
  ctx.bezierCurveTo(2.998, 8.82, 8.818, 3, 15.998, 3)
  ctx.closePath()
  ctx.fill('evenodd')
  ctx.stroke()
  ctx.restore()
  ctx.save()
  ctx.fillStyle = color3Css
  ctx.font = '   15px '
  ctx.globalAlpha = 0.2
  ctx.beginPath()
  ctx.moveTo(16, 6)
  ctx.bezierCurveTo(21.523, 6, 26, 10.477, 26, 16)
  ctx.bezierCurveTo(26, 21.523, 21.523, 26, 16, 26)
  ctx.bezierCurveTo(10.477, 26, 6, 21.523, 6, 16)
  ctx.bezierCurveTo(6, 10.477, 10.477, 6, 16, 6)
  ctx.closePath()
  ctx.fill('evenodd')
  ctx.stroke()
  ctx.restore()
  ctx.save()
  ctx.fillStyle = color3Css
  ctx.font = '   15px '
  ctx.globalAlpha = 0.2
  ctx.beginPath()
  ctx.moveTo(16, 8)
  ctx.bezierCurveTo(20.418, 8, 24, 11.582, 24, 16)
  ctx.bezierCurveTo(24, 20.418, 20.418, 24, 16, 24)
  ctx.bezierCurveTo(11.582, 24, 8, 20.418, 8, 16)
  ctx.bezierCurveTo(8, 11.582, 11.582, 8, 16, 8)
  ctx.closePath()
  ctx.fill('evenodd')
  ctx.stroke()
  ctx.restore()
  ctx.save()
  ctx.fillStyle = color3Css
  ctx.font = '   15px '
  ctx.globalAlpha = 0.2
  ctx.beginPath()
  ctx.moveTo(16, 10)
  ctx.bezierCurveTo(19.314, 10, 22, 12.686, 22, 16)
  ctx.bezierCurveTo(22, 19.314, 19.314, 22, 16, 22)
  ctx.bezierCurveTo(12.686, 22, 10, 19.314, 10, 16)
  ctx.bezierCurveTo(10, 12.686, 12.686, 10, 16, 10)
  ctx.closePath()
  ctx.fill('evenodd')
  ctx.stroke()
  ctx.restore()
  ctx.save()
  ctx.fillStyle = color3Css
  ctx.font = '   15px '
  ctx.globalAlpha = 0.2
  ctx.beginPath()
  ctx.moveTo(16, 12)
  ctx.bezierCurveTo(18.209, 12, 20, 13.791, 20, 16)
  ctx.bezierCurveTo(20, 18.209, 18.209, 20, 16, 20)
  ctx.bezierCurveTo(13.791, 20, 12, 18.209, 12, 16)
  ctx.bezierCurveTo(12, 13.791, 13.791, 12, 16, 12)
  ctx.closePath()
  ctx.fill('evenodd')
  ctx.stroke()
  ctx.restore()
  ctx.restore()
}

// This function takes an image or canvas and uses it as a template
// to "stamp" the pin with a white image outlined in black.  The color
// values of the input image are ignored completely and only the alpha
// values are used.
function drawIcon (context2D, image, size) {
  // Size is the largest image that looks good inside of pin box.
  // const imageSize = size / 2.0
  const sizeX = image.width
  const sizeY = image.height

  // if (image.width > image.height) {
  //   sizeY = imageSize * (image.height / image.width)
  //   sizeY *= 1.4
  //   sizeX *= 1.4
  // } else if (image.width < image.height) {
  //   sizeX = imageSize * (image.width / image.height)
  // }

  // x and y are the center of the pin box
  const x = Math.round((size - sizeX) / 2)
  const y = Math.round((size - sizeY) / 2)

  // context2D.globalCompositeOperation = 'destination-out'
  // context2D.drawImage(image, x - 1, y, sizeX, sizeY)
  // context2D.drawImage(image, x, y - 1, sizeX, sizeY)
  // context2D.drawImage(image, x + 1, y, sizeX, sizeY)
  // context2D.drawImage(image, x, y + 1, sizeX, sizeY)

  // context2D.globalCompositeOperation = 'destination-over'
  // context2D.fillStyle = Cesium.Color.BLACK.toCssColorString()
  // context2D.fillRect(x - 1, y - 1, sizeX + 2, sizeY + 2)

  context2D.globalCompositeOperation = 'destination-out'
  context2D.drawImage(image, x, y, sizeX, sizeY)

  context2D.globalCompositeOperation = 'destination-over'
  context2D.fillStyle = Cesium.Color.WHITE.toCssColorString()
  context2D.fillRect(x - 1, y - 2, sizeX + 2, sizeY + 2)
}

const stringifyScratch = new Array(4)
function createPin (url, label, color1, color2, color3, size, cache) {
  // Use the parameters as a unique ID for caching.
  stringifyScratch[0] = url
  stringifyScratch[1] = label
  stringifyScratch[2] = color1
  stringifyScratch[3] = size
  stringifyScratch[4] = color2
  stringifyScratch[5] = color3
  const id = JSON.stringify(stringifyScratch)

  const item = cache[id]
  if (Cesium.defined(item)) {
    return item
  }

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const context2D = canvas.getContext('2d')
  drawPin(context2D, color1, color2, color3, size)

  if (Cesium.defined(url)) {
    const resource = Cesium.Resource.createIfNeeded(url)

    // If we have an image url, load it and then stamp the pin.
    const promise = resource.fetchImage().then(function (image) {
      drawIcon(context2D, image, size)
      cache[id] = canvas
      return canvas
    })
    cache[id] = promise
    return promise
  } else if (Cesium.defined(label)) {
    // If we have a label, write it to a canvas and then stamp the pin.
    const image = Cesium.writeTextToCanvas(label, {
      font: '12px Source Han Sans SC',
      padding: 2
    })
    drawIcon(context2D, image, size)
  }

  cache[id] = canvas
  return canvas
}
export default PinBuilder
